import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyLink,
  SkyMessageBar,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { Fragment, useId, useMemo, useState, type CSSProperties, type ReactElement } from "react";
import { Link } from "wouter";
import type {
  RunConnectionSlice,
  RunSummaryRow,
  RunTransformStepFacts,
  StepStatus,
} from "@shared/types/runs";
import { IMPORT_CAUSES } from "@shared/types/importCauses";
import type { ScheduleSkip, ScheduleSkipReason } from "@shared/types/schedules";
import { ConnectorLogo } from "@/components/connections/ConnectorLogo";
import { ImportProgress } from "@/components/runs/ImportProgress";
import { StartedByCell } from "@/components/runs/RunsTable";
import {
  formatRowsLine,
  stepStatusPresentation,
  transformLineParts,
} from "@/components/runs/runFormatting";
import { useConnectors } from "@/hooks/use-connectors";
import { useDataflows } from "@/hooks/use-dataflows";
import { RUN_POLL_INTERVAL_LIVE_MS, usePollWhile } from "@/hooks/use-runs";
import { useScheduleHistory, useScheduleSkips } from "@/hooks/use-schedule-history";
import { pixelColumnLayout } from "@/lib/grid";
import { failureMessage } from "@/lib/api";
import { ChevronDownIcon, ChevronRightIcon, RetryIcon } from "@/lib/icons";
import { usePermissions } from "@/hooks/use-permissions";
import { ROUTES, absolutePath, connectionPath } from "@/lib/routes";
import { ASK_ADMIN_DESTINATION } from "@/lib/transformations/dbtSettings";
import { deriveRunActivity, seedFromRunSummary } from "@/lib/runs/runActivity";
import { instantZoneName } from "./scheduleForm";
import { formatInstant, instantHoverTitle, zoneCaption } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { RunningDuration } from "@/components/runs/RunningDuration";

/**
 * A schedule's History (Session 11, P3; owner item 3, D4-D6), the second view of the schedule
 * editor. It replaces Session 10's "Recent imports".
 *
 * ONE list read, `GET /runs?scheduleId=&include=connections&limit=20` (not audited), with Load
 * more on the list's cursor. One row per firing, newest first: when it started (in the
 * schedule's zone), who started it, its status (`ImportProgress density="row"`, from the row
 * alone), duration and rows. Opening a firing shows, from the SAME read (no request per row):
 *
 * - one row per connection, in the schedule's order: logo and name, started, finished, rows
 *   extracted and loaded, status, "Steps" (the connection's own Runs view with this run open)
 *   and "View import";
 * - one transformation row (D5: one dbt invocation per firing, so one row, not one per
 *   transformation): "Transformations: {names}", started, finished, "{m} models · {t} tests
 *   passed", status, opening the run page on its Transformations view.
 *
 * The grid has no row expansion, so the firings are cut into consecutive grids that each END at
 * an open firing, and that firing's region follows its grid (the pattern of the Transformations
 * History). While a firing on the first page is queued or running, the list is re-read every
 * 15 s; nothing is read once they are all over.
 *
 * Viewer-safe: nothing here writes.
 */

/**
 * The fire time in the reader's display zone, "Sep 19, 2:00 AM MST" (#983, as the list and Runs show it).
 */
function firedAt(iso: string, zone: string): string {
  try {
    const at = new Date(iso);
    const text = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(at);
    return `${text} ${instantZoneName(zone, at)}`;
  } catch {
    return formatInstant(iso, zone, "short", { named: true });
  }
}

/** The local calendar day of an instant in a zone, for "same day as the firing". */
function dayIn(iso: string, zone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: zone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

/**
 * A connection's or the transformations' start or finish, in the schedule's zone: "5:00 PM" on
 * the firing's own day, "Sep 20, 1:00 AM" on another. A dash when there is none yet.
 */
function clockTime(iso: string | null | undefined, zone: string, firedIso: string): string {
  if (!iso) return "—";
  try {
    const sameDay = dayIn(iso, zone) === dayIn(firedIso, zone);
    return new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      ...(sameDay ? {} : { month: "short", day: "numeric" }),
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return formatInstant(iso, zone, "short", { named: true });
  }
}

/** One word for a connection's part of a firing, from its own steps. */
function sliceStatus(slice: RunConnectionSlice): StepStatus | null {
  const statuses = slice.steps.map((step) => step.status);
  if (statuses.length === 0) return null;
  for (const status of ["failed", "running", "cancelled", "pending"] as const) {
    if (statuses.includes(status)) return status;
  }
  return statuses.every((status) => status === "skipped") ? "skipped" : "succeeded";
}

/** The first of the slice's steps to start; the last to finish once none is still going. */
function sliceTimes(slice: RunConnectionSlice): {
  startedAt: string | null;
  finishedAt: string | null;
} {
  const starts = slice.steps.flatMap((step) => (step.startedAt ? [step.startedAt] : []));
  const finishes = slice.steps.flatMap((step) => (step.finishedAt ? [step.finishedAt] : []));
  const startedAt = starts.reduce<string | null>((a, b) => (a === null || b < a ? b : a), null);
  const finishedAt =
    slice.durationSeconds === null
      ? null
      : finishes.reduce<string | null>((a, b) => (a === null || b > a ? b : a), null);
  return { startedAt, finishedAt };
}

const DELETED_CONNECTION_LABEL = "Deleted connection";

/**
 * The name shown for a connection the list does not hold: deleted once the list was read, else
 * that the name could not be read, else that it is still loading.
 */
function missingConnectionLabel(listRead: boolean, listFailed: boolean): string {
  if (listRead) return DELETED_CONNECTION_LABEL;
  return listFailed ? CONNECTION_NAME_UNREAD : CONNECTION_NAME_PENDING;
}

/** While the connections list is still being read, or when it could not be read. */
const CONNECTION_NAME_PENDING = "Loading…";
const CONNECTION_NAME_UNREAD = "Connection name not available";

const OVERLAP_SENTENCE = "A later firing was skipped because this import was still running.";

const SKIPPED_FIRINGS_SENTENCE =
  "A skip date or a pause moves the next run past it, so it is listed here only when it stopped a firing that was already due. A firing skipped because the previous import was still running is also marked on that import.";

/** Why a firing made no run, as the History's skipped-firings list reads it. */
const SKIP_REASON_LABELS: Record<ScheduleSkipReason, string> = {
  previous_run_running: "The previous import was still running",
  skip_date: "A skip date",
  paused: "Paused",
  // 2026-09-28: the firing left these connections out; the others were imported.
  connection_disabled: "Connection disabled",
  // Round 8: the connection's target changed and it has not passed a connection test since.
  connection_untested: "Connection not tested since its target changed",
  // #1471: a Draft connection (not validated, a failed test, a credential missing) was left out.
  connection_not_ready: "Connection not ready",
  // Round 9 (B-28): another schedule's import (or one started by hand) held the connection.
  connection_busy: "The connection was in use by another import",
  // Round 9 (B-01): the schedule still named a connection that was deleted; it was dropped.
  connection_deleted: "Connection deleted, removed from this schedule",
  // #375, #923: nothing runs until the destination is set and has passed its test.
  // #1106: the next step follows the reader (`destinationNextStep`), not the label.
  destination_not_set: "Skipped: the destination is not set.",
  destination_not_ready: "Skipped: the destination has not passed its test.",
  destination_coming_soon:
    "Skipped: Databricks destinations are coming soon. Use Snowflake for now.",
};

/** "the schedule Daily Azure SQL import", or "an import started by hand". */
function holderPhrase(holderScheduleName: string | null | undefined): string {
  return holderScheduleName ? `the schedule ${holderScheduleName}` : "an import started by hand";
}

/**
 * Round 10 wave 2 (A1): a firing that still ran its free connections. "Imported 2 of 3: Azure
 * SQL was in use by the schedule Daily Azure SQL import".
 */
function partialBusyText(skip: ScheduleSkip, connectionNames: ConnectionNames): string {
  const holders = skip.holders ?? [];
  const busy = holders.length || skip.dataflowIds?.length || 0;
  const imported = skip.importedCount;
  const head =
    imported === undefined ? "Imported the others" : `Imported ${imported} of ${imported + busy}`;
  const parts = holders.map(
    (holder) =>
      `${connectionNames.names.get(holder.dataflowId) ?? connectionNames.missing} was in use by ${holderPhrase(holder.holderScheduleName)}`,
  );
  return parts.length > 0 ? `${head}: ${parts.join(" · ")}` : head;
}

/**
 * #1471: what a reader who cannot validate a connection (`dataflows.validate`, Contributor and
 * above) reads in place of the run page's fix.
 */
const NOT_READY_ASK = "Ask a contributor or an admin to validate the connection.";

/** #1471: what became of the rest of a firing that left a Draft connection out. */
function notReadyOutcome(skip: ScheduleSkip, count: number): string {
  const subject = count > 1 ? "They were" : "It was";
  if (!skip.firedRunId) return `${subject} left out of this firing, and nothing was imported.`;
  const imported = skip.importedCount;
  if (imported === undefined)
    return `${subject} left out of this firing. The others were imported.`;
  const others =
    imported === 1 ? "The other connection was" : `The other ${imported} connections were`;
  return `${subject} left out of this firing. ${others} imported.`;
}

/**
 * #1471: a firing that left out a Draft connection. It names each one as a link to it, says in the
 * past tense what the firing did, and gives the run page's fix for the same cause
 * (`IMPORT_CAUSES.platform.connection_not_ready`) to a reader who can validate, and who to ask to
 * anyone else. The import that went ahead is linked beside it (`PartialBusyLinks`).
 */
function NotReadySkip({
  skip,
  connectionNames,
  canValidate,
}: {
  skip: ScheduleSkip;
  connectionNames: ConnectionNames;
  canValidate: boolean;
}) {
  const ids = skip.dataflowIds ?? [];
  const label = SKIP_REASON_LABELS.connection_not_ready;
  if (ids.length === 0) {
    return (
      <SkyText size={200} color="muted">
        {label}
      </SkyText>
    );
  }
  return (
    <>
      <SkyText size={200} color="muted" testId="schedule-skip-not-ready">
        {label}:{" "}
        {ids.map((id, index) => (
          <Fragment key={id}>
            {index > 0 ? ", " : null}
            <SkyLink as={Link} href={absolutePath(connectionPath(id))}>
              {connectionNames.names.get(id) ?? connectionNames.missing}
            </SkyLink>
          </Fragment>
        ))}
        . {notReadyOutcome(skip, ids.length)}
      </SkyText>
      <SkyText size={200} color="muted" testId="schedule-skip-not-ready-next">
        {canValidate ? IMPORT_CAUSES.platform.connection_not_ready.fix : NOT_READY_ASK}
      </SkyText>
    </>
  );
}

/** "Connection disabled: Clinical SQL, Claims" from the ids the skip row names. */
function skipReasonText(skip: ScheduleSkip, connectionNames: ConnectionNames): string {
  if (skip.reason === "connection_busy" && skip.firedRunId) {
    return partialBusyText(skip, connectionNames);
  }
  if (skip.reason === "connection_busy") {
    // B-28: name the holder so the reason points at the right schedule.
    return skip.holderScheduleName
      ? `The connection was in use by the schedule ${skip.holderScheduleName}`
      : "The connection was in use by an import started by hand";
  }
  const label = SKIP_REASON_LABELS[skip.reason];
  // A deleted connection has no name left to read: the count says what happened.
  if (skip.reason === "connection_deleted") {
    const count = skip.dataflowIds?.length ?? 0;
    return count > 0
      ? `${label} (${count === 1 ? "1 connection" : `${count} connections`})`
      : label;
  }
  const namesConnections =
    skip.reason === "connection_disabled" || skip.reason === "connection_untested";
  if (!namesConnections || !skip.dataflowIds?.length) return label;
  const names = skip.dataflowIds.map(
    (id) => connectionNames.names.get(id) ?? connectionNames.missing,
  );
  return `${label}: ${names.join(", ")}`;
}

/** The skips whose fix is on the Destination tab. */
function isDestinationSkip(skip: ScheduleSkip): boolean {
  return skip.reason === "destination_not_set" || skip.reason === "destination_not_ready";
}

/**
 * #1106: a destination skip's next step. The Destination tab is Instance admin and above
 * (`instance.binding.read`): its holder gets a link there, anyone else reads who acts.
 */
function DestinationNextStep({ canOpenDestination }: { canOpenDestination: boolean }) {
  return canOpenDestination ? (
    <SkyLink
      as={Link}
      href={absolutePath(ROUTES.settingsDestination)}
      testId="schedule-skip-open-destination"
    >
      Open Destination
    </SkyLink>
  ) : (
    <SkyText size={200} color="muted" testId="schedule-skip-ask-admin">
      {ASK_ADMIN_DESTINATION}
    </SkyText>
  );
}

/** A local date (`YYYY-MM-DD`) with no instant: "Sep 17", read in UTC so it never moves a day. */
function skipDay(date: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
    }).format(new Date(`${date}T00:00:00Z`));
  } catch {
    return date;
  }
}

/**
 * The firings this schedule did not start, newest first (2026-09-28): when (in the schedule's
 * zone), why, and, for an overlap, the import that was still running. The runner writes one
 * `schedule.skip` row per skipped firing; a row written before the firing time was recorded
 * reads by its date.
 */
function SkippedFirings({
  skips,
  zone,
  connectionNames,
}: {
  skips: ScheduleSkip[];
  zone: string;
  connectionNames: ConnectionNames;
}) {
  const titleId = useId();
  const permissions = usePermissions();
  const canOpenDestination = permissions.canAny("instance.binding.read");
  const canValidate = permissions.canAny("dataflows.validate");
  return (
    <div className="flex flex-col gap-2">
      <SkyText as="h3" size={300} weight="semibold" block id={titleId}>
        Skipped firings
      </SkyText>
      <ul
        aria-labelledby={titleId}
        className="flex flex-col gap-1"
        data-testid="schedule-history-skipped"
      >
        {skips.map((skip) => {
          const when = skip.scheduledFor ? firedAt(skip.scheduledFor, zone) : skipDay(skip.date);
          return (
            <li
              key={`${skip.at}-${skip.reason}`}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1"
            >
              <SkyText size={200} font="numeric">
                {skip.scheduledFor ? <time dateTime={skip.scheduledFor}>{when}</time> : when}
              </SkyText>
              {skip.reason === "connection_not_ready" ? (
                <NotReadySkip
                  skip={skip}
                  connectionNames={connectionNames}
                  canValidate={canValidate}
                />
              ) : (
                <SkyText size={200} color="muted">
                  {skipReasonText(skip, connectionNames)}
                </SkyText>
              )}
              {isDestinationSkip(skip) ? (
                <DestinationNextStep canOpenDestination={canOpenDestination} />
              ) : null}
              {skip.runId ? (
                <SkyLink
                  as={Link}
                  href={absolutePath(`/runs/${skip.runId}`)}
                  aria-label={`View the import that ${skip.reason === "connection_busy" ? "held the connection" : "was still running"} at ${when}`}
                >
                  View import
                </SkyLink>
              ) : null}
              {skip.firedRunId ? <PartialBusyLinks skip={skip} when={when} /> : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** A partly skipped firing's links: the import it ran, then each import that held a connection. */
function PartialBusyLinks({ skip, when }: { skip: ScheduleSkip; when: string }) {
  const holderRuns = [...new Set((skip.holders ?? []).map((holder) => holder.runId))];
  return (
    <>
      <SkyLink
        as={Link}
        href={absolutePath(`/runs/${skip.firedRunId}`)}
        aria-label={`View the import this firing ran at ${when}`}
      >
        View import
      </SkyLink>
      {holderRuns.map((runId) => (
        <SkyLink
          key={runId}
          as={Link}
          href={absolutePath(`/runs/${runId}`)}
          aria-label={`View the import that held the connection at ${when}`}
        >
          View the other import
        </SkyLink>
      ))}
    </>
  );
}

/** The firings, cut into grids that each end at an open firing. */
function segmentsOf(rows: RunSummaryRow[], expanded: readonly string[]): RunSummaryRow[][] {
  const segments: RunSummaryRow[][] = [];
  let current: RunSummaryRow[] = [];
  for (const row of rows) {
    current.push(row);
    if (expanded.includes(row.runId)) {
      segments.push(current);
      current = [];
    }
  }
  if (current.length > 0) segments.push(current);
  return segments;
}

type FiringPart =
  | { kind: "connection"; key: string; slice: RunConnectionSlice }
  | { kind: "transform"; key: string; step: RunTransformStepFacts };

function StatusBadge({ status, testId }: { status: StepStatus | null; testId: string }) {
  if (!status) {
    return (
      <SkyText size={200} color="muted" testId={testId}>
        —
      </SkyText>
    );
  }
  const presentation = stepStatusPresentation(status);
  return (
    <SkyBadge appearance="tint" color={presentation.color} testId={testId}>
      {presentation.label}
    </SkyBadge>
  );
}

/** A connector's label and logo, from the connectors list. */
interface ConnectorFacts {
  displayName: string;
  iconUrl: string | null;
}

/** The names the join found, and what an id it did not find reads. */
interface ConnectionNames {
  names: ReadonlyMap<string, string>;
  missing: string;
}

function regionId(runId: string): string {
  return `schedule-firing-parts-${runId}`;
}

/** An open firing: one row per connection, then the transformations' one row. */
function FiringParts({
  row,
  zone,
  firedLabel,
  connectionNames,
  connectorOf,
  connectors,
}: {
  row: RunSummaryRow;
  zone: string;
  firedLabel: string;
  connectionNames: ConnectionNames;
  connectorOf: ReadonlyMap<string, string>;
  connectors: ReadonlyMap<string, ConnectorFacts>;
}) {
  const parts: FiringPart[] = [
    ...(row.connections ?? []).map((slice): FiringPart => ({
      kind: "connection",
      key: slice.dataflowId,
      slice,
    })),
    ...(row.transformStep
      ? [{ kind: "transform", key: "transform", step: row.transformStep } as FiringPart]
      : []),
  ];

  const columns: SkyDataGridColumn<FiringPart>[] = [
    {
      key: "name",
      header: "Connection",
      width: "26%",
      renderCell: (_value, part) => {
        if (part.kind === "transform") {
          const names = row.dbtJobNames ?? [];
          return (
            <span data-testid={`schedule-firing-name-${row.runId}-transform`}>
              <SkyText
                size={200}
                weight="semibold"
                testId={`schedule-firing-transform-${row.runId}`}
              >
                {names.length > 0 ? `dbt jobs: ${names.join(", ")}` : "dbt jobs"}
              </SkyText>
            </span>
          );
        }
        const id = part.slice.dataflowId;
        const name = connectionNames.names.get(id);
        const connector = connectorOf.get(id);
        const label = name ?? connectionNames.missing;
        return (
          <span
            className="flex min-w-0 items-center gap-2"
            data-testid={`schedule-firing-connection-${row.runId}-${id}`}
          >
            {/* The connector's logo, or its first letter (the Connections list's rule); a
                connection that is gone has no connector to show, so its own first letter. While
                the list is unread there is no name to take a letter from ("?", not the "L" of
                "Loading…"). */}
            <ConnectorLogo
              iconUrl={connector ? connectors.get(connector)?.iconUrl : undefined}
              name={
                (connector ? connectors.get(connector)?.displayName : undefined) ??
                (name || connectionNames.missing === DELETED_CONNECTION_LABEL ? label : "")
              }
            />
            <SkyText
              size={200}
              truncate
              color={name ? undefined : "muted"}
              testId={`schedule-firing-name-${row.runId}-${id}`}
            >
              {label}
            </SkyText>
          </span>
        );
      },
    },
    {
      key: "startedAt",
      header: "Started",
      width: "12%",
      renderCell: (_value, part) => {
        const startedAt =
          part.kind === "transform" ? part.step.startedAt : sliceTimes(part.slice).startedAt;
        return (
          <SkyText
            size={200}
            font="numeric"
            testId={`schedule-firing-part-started-${row.runId}-${part.key}`}
          >
            {clockTime(startedAt, zone, row.createdAt)}
          </SkyText>
        );
      },
    },
    {
      key: "finishedAt",
      header: "Finished",
      width: "12%",
      renderCell: (_value, part) => {
        const finishedAt =
          part.kind === "transform" ? part.step.finishedAt : sliceTimes(part.slice).finishedAt;
        return (
          <SkyText
            size={200}
            font="numeric"
            testId={`schedule-firing-part-finished-${row.runId}-${part.key}`}
          >
            {clockTime(finishedAt, zone, row.createdAt)}
          </SkyText>
        );
      },
    },
    {
      key: "rows",
      header: "Result",
      width: "22%",
      renderCell: (_value, part) => {
        if (part.kind === "transform") {
          const line = transformLineParts(row.transformTotals);
          return (
            <SkyText
              size={200}
              font="numeric"
              testId={`schedule-firing-part-result-${row.runId}-transform`}
            >
              {line.length === 0
                ? "—"
                : line.map((item, index) => (
                    <span
                      key={item.text}
                      className={item.tone === "danger" ? "text-destructive" : undefined}
                    >
                      {index > 0 ? " · " : ""}
                      {item.text}
                    </span>
                  ))}
            </SkyText>
          );
        }
        return (
          <SkyText
            size={200}
            font="numeric"
            testId={`schedule-firing-part-result-${row.runId}-${part.key}`}
          >
            {part.slice.totals ? formatRowsLine(part.slice.totals, { short: true }) : "—"}
          </SkyText>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      width: "11%",
      renderCell: (_value, part) => (
        <StatusBadge
          status={part.kind === "transform" ? part.step.status : sliceStatus(part.slice)}
          testId={`schedule-firing-part-status-${row.runId}-${part.key}`}
        />
      ),
    },
    {
      key: "actions",
      header: "Open",
      width: "17%",
      align: "right",
      renderCell: (_value, part) => {
        if (part.kind === "transform") {
          return (
            <SkyLink
              as={Link}
              href={absolutePath(`/runs/${row.runId}?view=transform`)}
              size="small"
              aria-label={`View the dbt results of the firing at ${firedLabel}`}
              testId={`schedule-firing-transform-view-${row.runId}`}
            >
              View dbt results
            </SkyLink>
          );
        }
        const id = part.slice.dataflowId;
        const name = connectionNames.names.get(id) ?? connectionNames.missing;
        return (
          <span className="flex flex-wrap items-center justify-end gap-3">
            {connectionNames.names.has(id) ? (
              <SkyLink
                as={Link}
                href={absolutePath(
                  `${connectionPath(id)}?tab=runs&run=${encodeURIComponent(row.runId)}`,
                )}
                size="small"
                aria-label={`Steps of ${name} in the firing at ${firedLabel}`}
                testId={`schedule-firing-steps-${row.runId}-${id}`}
              >
                Steps
              </SkyLink>
            ) : null}
            <SkyLink
              as={Link}
              href={absolutePath(`/runs/${row.runId}`)}
              size="small"
              aria-label={`View import for ${name} at ${firedLabel}`}
              testId={`schedule-firing-view-${row.runId}-${id}`}
            >
              View import
            </SkyLink>
          </span>
        );
      },
    },
  ];

  return (
    <section
      id={regionId(row.runId)}
      aria-label={`Firing at ${firedLabel}, by connection`}
      className="flex flex-col gap-2 rounded-lg border border-border bg-muted p-3"
      data-testid={regionId(row.runId)}
    >
      {parts.length === 0 ? (
        <SkyText size={200} color="muted" testId={`${regionId(row.runId)}-none`}>
          No connection or dbt job has started in this firing yet.
        </SkyText>
      ) : (
        <SkyDataGrid<FiringPart>
          data={parts}
          columns={columns}
          getRowId={(part) => part.key}
          bordered
          testId={`${regionId(row.runId)}-grid`}
        />
      )}
    </section>
  );
}

/** A failed read after the first page: more firings, or a refresh of the ones shown. */
function LaterReadError({
  error,
  nextPageFailed,
  onRetry,
}: {
  error: unknown;
  nextPageFailed: boolean;
  onRetry: () => void;
}) {
  return (
    <SkyMessageBar
      intent="error"
      layout="multiline"
      title={
        nextPageFailed
          ? "We could not load more firings"
          : "We could not refresh this schedule's history"
      }
      message={failureMessage(error, "Try again.")}
      // Load more is its own retry; a refresh that failed is read again from here, not only
      // by the next poll (which stops once no firing moves).
      actions={
        nextPageFailed ? undefined : (
          <SkyButton size="small" icon={RetryIcon} onClick={onRetry}>
            Try again
          </SkyButton>
        )
      }
      testId="schedule-history-more-error"
    />
  );
}

/** Load more; a press while a page is being read is ignored. */
function LoadMoreFirings({ fetching, onLoadMore }: { fetching: boolean; onLoadMore: () => void }) {
  return (
    <div>
      <SkyButton
        appearance="secondary"
        aria-busy={fetching ? true : undefined}
        testId="schedule-history-load-more"
        onClick={() => {
          if (!fetching) onLoadMore();
        }}
      >
        {fetching ? "Loading more firings…" : "Load more"}
      </SkyButton>
    </div>
  );
}

export interface ScheduleHistoryProps {
  scheduleId: string;
  scheduleName: string;
}

export function ScheduleHistory({ scheduleId, scheduleName }: ScheduleHistoryProps) {
  // #983: the reader's display zone, as the Schedules list and Runs show the same run. The zone
  // is named on every time and in the caption under the title.
  const timezone = useDisplayTz();
  const runs = useScheduleHistory(scheduleId);
  const skips = useScheduleSkips(scheduleId);
  const rows = useMemo(() => (runs.data?.pages ?? []).flatMap((page) => page.rows), [runs.data]);
  // Names and logos from the existing unaudited lists, joined here (contract §4.2): the slice
  // carries only the connection's id.
  const dataflows = useDataflows();
  const connectorList = useConnectors();

  const connectionNames = useMemo<ConnectionNames>(() => {
    const names = new Map<string, string>();
    for (const flow of dataflows.data ?? []) names.set(flow.id, flow.displayName);
    // Only a list that was read can say a connection is gone.
    const missing = missingConnectionLabel(dataflows.data !== undefined, dataflows.isError);
    return { names, missing };
  }, [dataflows.data, dataflows.isError]);
  const connectorOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const flow of dataflows.data ?? []) map.set(flow.id, flow.connectorName);
    return map;
  }, [dataflows.data]);
  const connectors = useMemo(() => {
    const map = new Map<string, ConnectorFacts>();
    for (const connector of connectorList.data ?? []) {
      map.set(connector.name, {
        displayName: connector.displayName,
        iconUrl: connector.iconUrl ?? null,
      });
    }
    return map;
  }, [connectorList.data]);

  // A firing still moving is re-read with the list (not audited) every 15 seconds, one read for
  // every row and every open firing; nothing is read once they have all finished. Only the first
  // page decides: a live firing is at the top.
  const anyMoving = (runs.data?.pages[0]?.rows ?? []).some(
    (row) => row.status === "queued" || row.status === "running",
  );
  usePollWhile(anyMoving, RUN_POLL_INTERVAL_LIVE_MS, () => {
    void runs.refetch();
  });

  const [expanded, setExpanded] = useState<string[]>([]);
  const toggle = (runId: string): void =>
    setExpanded((open) =>
      open.includes(runId) ? open.filter((id) => id !== runId) : [...open, runId],
    );

  const columns: SkyDataGridColumn<RunSummaryRow>[] = [
    {
      key: "expand",
      header: "Details",
      // #1196: a pixel width holds the whole header (its label and 32 px of padding) at 1024.
      width: "88px",
      renderCell: (_value, row) => {
        const open = expanded.includes(row.runId);
        return (
          <SkyButton
            appearance="subtle"
            size="small"
            icon={open ? ChevronDownIcon : ChevronRightIcon}
            aria-expanded={open}
            aria-controls={open ? regionId(row.runId) : undefined}
            // Names what opens: the transformations row only when the firing has one.
            aria-label={`${open ? "Hide" : "Show"} the connections${row.transformStep ? " and dbt jobs" : ""} of the firing at ${firedAt(row.createdAt, timezone)}`}
            testId={`schedule-firing-toggle-${row.runId}`}
            onClick={() => toggle(row.runId)}
          />
        );
      },
    },
    {
      key: "createdAt",
      header: "Started at",
      width: "17%",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric" testId={`schedule-firing-${row.runId}`}>
          <time dateTime={row.createdAt} title={instantHoverTitle(row.createdAt, timezone)}>
            {firedAt(row.createdAt, timezone)}
          </time>
        </SkyText>
      ),
    },
    {
      key: "startedBy",
      header: "Started by",
      width: "15%",
      renderCell: (_value, row) => (
        <StartedByCell row={row} scheduleNames={new Map([[scheduleId, scheduleName]])} />
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "26%",
      renderCell: (_value, row) => {
        const activity = deriveRunActivity({
          runId: row.runId,
          dataflowId: null,
          seed: seedFromRunSummary(row),
        });
        const label = `${scheduleName} at ${firedAt(row.createdAt, timezone)}`;
        return (
          <div className="flex min-w-0 flex-col gap-1">
            <ImportProgress
              activity={activity}
              density="row"
              // "View import for Nightly HR at Sep 19, 2:00 AM MST": one name per row, so a
              // screen reader's link list tells the rows apart.
              connectionName={label}
              announce={false}
              testId={`schedule-firing-progress-${row.runId}`}
            />
            {row.overlapSkipped ? (
              <SkyText
                size={200}
                color="muted"
                block
                testId={`schedule-firing-overlap-${row.runId}`}
              >
                {OVERLAP_SENTENCE}
              </SkyText>
            ) : null}
          </div>
        );
      },
    },
    {
      key: "durationSeconds",
      header: "Duration",
      width: "104px",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          <RunningDuration
            seconds={row.durationSeconds}
            runningSince={row.status === "running" ? row.startedAt : null}
          />
        </SkyText>
      ),
    },
    {
      key: "rows",
      header: "Rows",
      width: "26%",
      align: "right",
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric" testId={`schedule-firing-rows-${row.runId}`}>
          {row.totals ? formatRowsLine(row.totals, { short: true }) : "—"}
        </SkyText>
      ),
    },
  ];

  // #1196: Details and Duration are pixel columns, so the percent columns share what they leave
  // and the table has a floor (a narrow pane scrolls the region sideways instead of squeezing
  // Started at or Status). Minimums are the header plus 32 px, a date with its zone name, and the
  // status line.
  const layout = pixelColumnLayout(columns, {
    minimums: { createdAt: 150, startedBy: 110, status: 180, rows: 110 },
    selection: false,
  });
  const gridClass = "[&_table]:min-w-(--history-grid-floor)";

  const segments = segmentsOf(rows, expanded);

  /** The firings: the first read failed, none yet (or loading), or one grid per segment. */
  const renderFirings = (): ReactElement => {
    if (runs.isError && rows.length === 0) {
      return (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="We could not load this schedule's history"
          message={failureMessage(runs.error, "Try again.")}
          actions={
            <SkyButton size="small" icon={RetryIcon} onClick={() => void runs.refetch()}>
              Try again
            </SkyButton>
          }
          testId="schedule-history-error"
        />
      );
    }
    if (runs.isLoading || rows.length === 0) {
      return (
        <SkyDataGrid<RunSummaryRow>
          data={[]}
          columns={layout.columns}
          className={gridClass}
          getRowId={(row) => row.runId}
          loading={runs.isLoading}
          bordered
          emptyMessage="This schedule has not started an import yet."
          testId="schedule-history-grid"
        />
      );
    }
    return (
      <div
        className="flex flex-col gap-3"
        style={{ "--history-grid-floor": `${layout.floor}px` } as CSSProperties}
      >
        {segments.map((segment, index) => {
          const first = segment[0] as RunSummaryRow;
          const last = segment[segment.length - 1] as RunSummaryRow;
          return (
            <Fragment key={first.runId}>
              <SkyDataGrid<RunSummaryRow>
                data={segment}
                columns={layout.columns}
                className={gridClass}
                getRowId={(row) => row.runId}
                bordered
                testId={
                  index === 0 ? "schedule-history-grid" : `schedule-history-grid-${first.runId}`
                }
              />
              {expanded.includes(last.runId) ? (
                <FiringParts
                  row={last}
                  zone={timezone}
                  firedLabel={firedAt(last.createdAt, timezone)}
                  connectionNames={connectionNames}
                  connectorOf={connectorOf}
                  connectors={connectors}
                />
              ) : null}
            </Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <section
      aria-labelledby="schedule-history-title"
      className="flex flex-col gap-3"
      data-testid="schedule-history"
    >
      <div className="flex flex-col gap-1">
        <SkyText as="h2" size={400} weight="semibold" block id="schedule-history-title">
          History
        </SkyText>
        <SkyText size={200} color="muted" block>
          Each firing of {scheduleName}, newest first. Open one to see each connection and the
          transformations.
        </SkyText>
        <SkyText size={200} color="muted" block testId="schedule-history-zone">
          {zoneCaption(timezone)}
        </SkyText>
      </div>

      {renderFirings()}

      {runs.isError && rows.length > 0 ? (
        <LaterReadError
          error={runs.error}
          nextPageFailed={runs.isFetchNextPageError}
          onRetry={() => void runs.refetch()}
        />
      ) : null}

      {runs.hasNextPage ? (
        <LoadMoreFirings
          fetching={runs.isFetchingNextPage}
          onLoadMore={() => void runs.fetchNextPage()}
        />
      ) : null}

      {skips.data && skips.data.items.length > 0 ? (
        <SkippedFirings
          skips={skips.data.items}
          zone={timezone}
          connectionNames={connectionNames}
        />
      ) : null}

      <SkyText size={200} color="muted" block testId="schedule-history-skips">
        {SKIPPED_FIRINGS_SENTENCE}
      </SkyText>
    </section>
  );
}
